import { errorEnvelopeSchema } from "@egalregal/shared";

/**
 * The error vocabulary behind the API seam (request-client-seam 01).
 *
 * A server that answered with a failing status throws an `ApiError` (or one of
 * its subclasses); a request that never reached a server throws a plain
 * `Error` sibling instead. Callers can therefore ask `instanceof ApiError`
 * for "the API said no" and `instanceof UnreachableError` for "nobody was
 * there".
 */

/** A failing status from a reachable server, carrying the parsed body. */
export class ApiError extends Error {
  readonly status: number;
  /** The envelope's `code`, when the body carried one. */
  readonly code?: string;
  /** The parsed body — the error envelope for JSON responses, otherwise unknown. */
  readonly body?: unknown;

  constructor(
    status: number,
    body?: unknown,
    code: string | undefined = envelopeCode(body),
  ) {
    super(`API request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** 404 — the resource is not readable, or not there at all. */
export class NotFoundError extends ApiError {
  constructor(body?: unknown, code?: string) {
    super(404, body, code);
    this.name = "NotFoundError";
  }
}

/** 409 — the write was built from a stale version. */
export class ConflictError extends ApiError {
  constructor(body?: unknown, code?: string) {
    super(409, body, code);
    this.name = "ConflictError";
  }
}

/** 403 — the caller may not perform this request. */
export class ForbiddenError extends ApiError {
  constructor(body?: unknown, code?: string) {
    super(403, body, code);
    this.name = "ForbiddenError";
  }
}

/**
 * No server answered: the transport rejected (offline, DNS, aborted socket).
 * Deliberately not an `ApiError` — there is no status or envelope.
 */
export class UnreachableError extends Error {
  constructor(path: string, options?: ErrorOptions) {
    super(`Network request to ${path} failed`, options);
    this.name = "UnreachableError";
  }
}

/**
 * A server answered successfully but the payload did not match the schema.
 * Deliberately not an `ApiError` — the response itself is the fault.
 */
export class InvalidResponseError extends Error {
  constructor(path: string, options?: ErrorOptions) {
    super(`API returned an unexpected shape for ${path}`, options);
    this.name = "InvalidResponseError";
  }
}

/**
 * A caller passed a body the shared request schema rejects. Deliberately not an
 * `ApiError` — no request was made.
 */
export class InvalidInputError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "InvalidInputError";
  }
}

/** Build the typed error for a failing status. */
export function apiErrorFor(status: number, body: unknown): ApiError {
  if (status === 404) return new NotFoundError(body);
  if (status === 409) return new ConflictError(body);
  if (status === 403) return new ForbiddenError(body);
  return new ApiError(status, body);
}

/** Pull the uniform envelope's `code` out of a parsed body, when there is one. */
function envelopeCode(body: unknown): string | undefined {
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error.code : undefined;
}
